import type { OtherUserInfo } from '@/apps/messages/helpers/otherUserInfo';

export type RequestKind = 'booking' | 'interview';

export type SeekerAdminMessageData =
  | { type: 'interview_request_created'; kind: RequestKind; interviewId: string }
  | { type: 'interview_contract_accepted'; kind: RequestKind; contractId: string; otherUserName: string }
  | { type: 'job_application_replied_parent'; jobUuid: string; jobAppUuid: string; otherUserName: string }
  | { type: 'interview_declined'; kind: RequestKind; otherUserName: string }
  | { type: 'interview_canceled'; kind: RequestKind }
  | { type: 'parent_contract_cancelation'; kind: RequestKind }
  | { type: 'sitter_contract_cancelation'; kind: RequestKind; otherUserName: string }
  | { type: 'job_shared_parent'; jobId: string; otherUserName: string };

export type ProviderAdminMessageData =
  | { type: 'job_application_replied_sitter'; jobId: string; otherUserName: string }
  | { type: 'interview_request_received'; kind: RequestKind; interviewId: string; otherUserName: string }
  | { type: 'interview_contract_accepted_sitter'; kind: RequestKind; contractId: string; otherUserName: string }
  | { type: 'interview_declined_sitter'; kind: RequestKind; otherUserName: string }
  | { type: 'interview_canceled_sitter'; kind: RequestKind; otherUserName: string }
  | { type: 'parent_contract_cancelation_sitter'; kind: RequestKind; otherUserName: string }
  | { type: 'sitter_contract_cancelation_sitter'; kind: RequestKind }
  | { type: 'job_shared_sitter'; jobId: string; otherUserName: string };

export type AdminMessageData = SeekerAdminMessageData | ProviderAdminMessageData;

interface AdminMessage {
  messageId?: string | number;
  metaArrays?: Array<{ key?: string; value?: string[] | string }>;
  /** Sendbird custom / payload field; often JSON with the same keys as meta_arrays. */
  data?: string;
  extendedMessagePayload?: Record<string, unknown>;
}

const readMetaArrayValue = (metaArrays: AdminMessage['metaArrays'], key: string): string | undefined => {
  const value = metaArrays?.find((item) => item.key === key)?.value;
  if (Array.isArray(value)) return value.toString();
  return value;
};

const metaArraysHaveKeys = (meta: AdminMessage['metaArrays'] | undefined): boolean =>
  !!meta?.some((item) => typeof item?.key === 'string' && item.key.length > 0);

const getStoredMetaArrays = (message: AdminMessage): AdminMessage['metaArrays'] => {
  const live = message.metaArrays;
  if (!message.messageId || typeof sessionStorage === 'undefined') return live;

  const id = String(message.messageId);
  const storedRaw = sessionStorage.getItem(id);
  let stored: AdminMessage['metaArrays'] | undefined;
  if (storedRaw) {
    try {
      stored = JSON.parse(storedRaw);
    } catch (_error) {
      stored = undefined;
    }
  }

  if (metaArraysHaveKeys(live)) {
    sessionStorage.setItem(id, JSON.stringify(live));
    return live;
  }

  if (metaArraysHaveKeys(stored)) {
    return stored;
  }

  if (live && live.length > 0) {
    sessionStorage.setItem(id, JSON.stringify(live));
  }

  return live;
};

const flattenStringFields = (input: unknown, out: Record<string, string>, depth = 0): void => {
  if (depth > 4 || input == null) return;
  if (typeof input !== 'object' || Array.isArray(input)) return;
  Object.entries(input as Record<string, unknown>).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = String(value);
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      flattenStringFields(value, out, depth + 1);
    }
  });
};

const collectAdminMetaExtras = (message: AdminMessage): Record<string, string> => {
  const out: Record<string, string> = {};
  if (message.extendedMessagePayload && typeof message.extendedMessagePayload === 'object') {
    flattenStringFields(message.extendedMessagePayload, out);
  }
  const dataRaw = message.data?.trim();
  if (dataRaw && dataRaw.startsWith('{')) {
    try {
      flattenStringFields(JSON.parse(dataRaw), out);
    } catch (_error) {
      /* data may be a non-JSON custom string */
    }
  }
  return out;
};

const META_KEY_ALIASES: Record<string, string[]> = {
  interview_request_id: ['interviewRequestId', 'interview_request_uuid', 'interviewRequestUuid', 'request_id', 'requestId'],
  interview_request_kind: ['interviewRequestKind', 'request_kind', 'requestKind'],
  interview_contract_id: ['interviewContractId', 'contract_id', 'contractId', 'booking_id', 'bookingId'],
  interview_contract_kind: ['interviewContractKind', 'contract_kind', 'contractKind'],
  interview_declined_id: ['interviewDeclinedId', 'declined_id', 'declinedId'],
  interview_canceled_id: ['interviewCanceledId', 'canceled_id', 'canceledId', 'cancelled_id', 'cancelledId'],
  parent_contract_cancelation_id: ['parentContractCancelationId', 'parent_contract_cancellation_id'],
  sitter_contract_cancelation_id: ['sitterContractCancelationId', 'sitter_contract_cancellation_id'],
  job_application_uuid: ['jobApplicationUuid', 'application_uuid', 'applicationUuid'],
  job_uuid: ['jobUuid', 'job_id', 'jobId'],
  job_state: ['jobState'],
};

const buildAdminGetValue = (metaArrays: AdminMessage['metaArrays'], extras: Record<string, string>) => {
  return (canonicalKey: string): string | undefined => {
    const names = META_KEY_ALIASES[canonicalKey] ? [canonicalKey, ...META_KEY_ALIASES[canonicalKey]] : [canonicalKey];
    const fromMeta = names.map((name) => readMetaArrayValue(metaArrays, name)).find(Boolean);
    if (fromMeta) return fromMeta;
    return names.map((name) => extras[name]).find(Boolean);
  };
};

// Helper to convert kind metadata to RequestKind enum
const normalizeRequestKind = (kindValue?: string): RequestKind => {
  return kindValue === 'booking' ? 'booking' : 'interview';
};

// Handler functions for different message types per account type
// eslint-disable-next-line no-unused-vars
type AdminMessageHandler = (getValue: (key: string) => string | undefined, otherUserName: string) => AdminMessageData | null;

const parentMessageHandlers: Record<string, AdminMessageHandler> = {
  interview_request_created: (getValue) => {
    const interviewId = getValue('interview_request_id');
    const kind = getValue('interview_request_kind');
    if (!interviewId || !kind) return null;
    return { type: 'interview_request_created', kind: normalizeRequestKind(kind), interviewId };
  },
  interview_contract_accepted: (getValue, otherUserName) => {
    const contractId = getValue('interview_contract_id');
    const kind = getValue('interview_contract_kind');
    if (!contractId || !kind) return null;
    return { type: 'interview_contract_accepted', kind: normalizeRequestKind(kind), contractId, otherUserName };
  },
  job_application_replied_parent: (getValue, otherUserName) => {
    const jobAppUuid = getValue('job_application_uuid');
    if (!jobAppUuid) return null;
    const jobUuid = getValue('job_uuid') || '';
    return { type: 'job_application_replied_parent', jobUuid, jobAppUuid, otherUserName };
  },
  interview_declined: (getValue, otherUserName) => {
    const declinedId = getValue('interview_declined_id');
    const kind = getValue('interview_request_kind');
    if (!declinedId || !kind) return null;
    return { type: 'interview_declined', kind: normalizeRequestKind(kind), otherUserName };
  },
  interview_canceled: (getValue) => {
    const canceledId = getValue('interview_canceled_id');
    const kind = getValue('interview_request_kind');
    if (!canceledId || !kind) return null;
    return { type: 'interview_canceled', kind: normalizeRequestKind(kind) };
  },
  parent_contract_cancelation: (getValue) => {
    const cancelationId = getValue('parent_contract_cancelation_id');
    const kind = getValue('interview_contract_kind');
    if (!cancelationId || !kind) return null;
    return { type: 'parent_contract_cancelation', kind: normalizeRequestKind(kind) };
  },
  sitter_contract_cancelation: (getValue, otherUserName) => {
    const cancelationId = getValue('sitter_contract_cancelation_id');
    const kind = getValue('interview_contract_kind');
    if (!cancelationId || !kind) return null;
    return { type: 'sitter_contract_cancelation', kind: normalizeRequestKind(kind), otherUserName };
  },
  job_shared_parent: (getValue, otherUserName) => {
    const jobState = getValue('job_state');
    if (jobState !== 'shared') return null;
    const jobId = getValue('job_uuid');
    if (!jobId) return null;
    return { type: 'job_shared_parent', jobId, otherUserName };
  },
};

const sitterMessageHandlers: Record<string, AdminMessageHandler> = {
  job_application_replied_sitter: (getValue, otherUserName) => {
    const jobAppUuid = getValue('job_application_uuid');
    if (!jobAppUuid) return null;
    const jobId = getValue('job_uuid') || '';
    return { type: 'job_application_replied_sitter', jobId, otherUserName };
  },
  interview_request_received: (getValue, otherUserName) => {
    const interviewId = getValue('interview_request_id');
    const kind = getValue('interview_request_kind');
    if (!interviewId || !kind) return null;
    return { type: 'interview_request_received', kind: normalizeRequestKind(kind), interviewId, otherUserName };
  },
  interview_contract_accepted_sitter: (getValue, otherUserName) => {
    const contractId = getValue('interview_contract_id');
    const kind = getValue('interview_contract_kind');
    if (!contractId || !kind) return null;
    return { type: 'interview_contract_accepted_sitter', kind: normalizeRequestKind(kind), contractId, otherUserName };
  },
  interview_declined_sitter: (getValue, otherUserName) => {
    const declinedId = getValue('interview_declined_id');
    const kind = getValue('interview_request_kind');
    if (!declinedId || !kind) return null;
    return { type: 'interview_declined_sitter', kind: normalizeRequestKind(kind), otherUserName };
  },
  interview_canceled_sitter: (getValue, otherUserName) => {
    const canceledId = getValue('interview_canceled_id');
    const kind = getValue('interview_request_kind');
    if (!canceledId || !kind) return null;
    return { type: 'interview_canceled_sitter', kind: normalizeRequestKind(kind), otherUserName };
  },
  parent_contract_cancelation_sitter: (getValue, otherUserName) => {
    const cancelationId = getValue('parent_contract_cancelation_id');
    const kind = getValue('interview_contract_kind');
    if (!cancelationId || !kind) return null;
    return { type: 'parent_contract_cancelation_sitter', kind: normalizeRequestKind(kind), otherUserName };
  },
  sitter_contract_cancelation_sitter: (getValue) => {
    const cancelationId = getValue('sitter_contract_cancelation_id');
    const kind = getValue('interview_contract_kind');
    if (!cancelationId || !kind) return null;
    return { type: 'sitter_contract_cancelation_sitter', kind: normalizeRequestKind(kind) };
  },
  job_shared_sitter: (getValue, otherUserName) => {
    const jobState = getValue('job_state');
    if (jobState !== 'shared') return null;
    const jobId = getValue('job_uuid');
    if (!jobId) return null;
    return { type: 'job_shared_sitter', jobId, otherUserName };
  },
};

export const parseAdminMessage = (
  message: AdminMessage,
  otherUserInfo: OtherUserInfo,
  accountType: 'parent' | 'sitter'
): AdminMessageData | null => {
  const metaArrays = getStoredMetaArrays(message);
  const extras = collectAdminMetaExtras(message);
  const getValue = buildAdminGetValue(metaArrays, extras);
  const otherUserName = otherUserInfo?.name || '';
  const handlers = accountType === 'parent' ? parentMessageHandlers : sitterMessageHandlers;

  // loops through metadata and displays in order of priority
  // i.e. message could have interview_request_id and job_application_uuid
  // so we need to check in order of priority and return the first one that matches
  return Object.values(handlers).reduce<AdminMessageData | null>(
    (found, handler) => found ?? handler(getValue, otherUserName),
    null
  );
};

// legacy pre-Sendbird messages
export type UnparsedAdminBodyContext = {
  accountType: 'parent' | 'sitter';
  otherUserName: string;
  currentUserNickname: string;
};

const TRANSLATION_MISSING_SNIPPET = /(?:^|\n)\s*translation missing:\s*[^\n]+/gi;

const capitalizeYouAfterSentenceBoundaries = (s: string): string =>
  s.replace(/(^|[.!?]\s*|\n\s*)(you)\b/g, (_, prefix: string) => `${prefix}You`);

export const formatUnparsedAdminMessageBody = (body: string, context: UnparsedAdminBodyContext): string => {
  if (!body) return body;

  const other = context.otherUserName.trim();
  const self = context.currentUserNickname.trim();
  const providerName = context.accountType === 'parent' ? other : self;
  const seekerName = context.accountType === 'parent' ? self : other;

  const providerDisplay = context.accountType === 'sitter' ? 'you' : providerName || 'Sitter';
  const seekerDisplay = context.accountType === 'parent' ? 'you' : seekerName || 'Parent';

  let out = body.replace(/\r\n/g, '\n').replace(TRANSLATION_MISSING_SNIPPET, '\n');
  out = out
    .replace(/%\{\s*provider_display_name\s*\}/gi, providerDisplay)
    .replace(/%\{\s*seeker_display_name\s*\}/gi, seekerDisplay);
  out = capitalizeYouAfterSentenceBoundaries(out);
  out = out.replace(/\n{3,}/g, '\n\n').trim();
  out = out.replace(/([A-Za-z])\.{2}(?=\s*$)/gm, '$1.');

  if (!out) return '';

  return out;
};
